// src/App.js
import { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";

// Layout — always loaded (no lazy: needed on every page)
import AlertBanner    from "./components/AlertBanner";
import Navbar         from "./components/Navbar";
import Footer         from "./components/Footer";
import GenericHero    from "./components/GenericHero";
import ErrorBoundary  from "./components/ErrorBoundary";

// Disaster FAB — 52px floating circle, bottom-right, all pages.
// Expands to a slide-up drawer with top-3 predictions + "View All" CTA.
// Auto-expands once on first critical event. Auto-collapses after 10s idle.
import DisasterFAB from "./components/disasters/DisasterFAB";
import DataAccessRequest from "./pages/DataAccessRequest";

// Pages — lazy loaded per route (reduces initial bundle ~60%)
const SHOW_DISASTER_FAB = false; // set to true to re-enable
const Home                = lazy(() => import("./pages/Home"));
const MapExplorer         = lazy(() => import("./pages/MapExplorer"));
const SpaceWeatherAlerts  = lazy(() => import("./pages/SpaceWeatherAlerts"));
const DataPortal          = lazy(() => import("./pages/DataPortal"));
const CommunityDashboards = lazy(() => import("./pages/CommunityDashboards"));
const Education           = lazy(() => import("./pages/Education"));

// Components used as pages — lazy loaded
const Benefits     = lazy(() => import("./components/Benefits"));
const Publications = lazy(() => import("./components/Publications"));
const Partners     = lazy(() => import("./components/Partners"));
const About        = lazy(() => import("./components/About"));
const Contact      = lazy(() => import("./components/Contact"));
const Resources    = lazy(() => import("./components/Resources"));

// Product components — lazy loaded
const DashboardTEC  = lazy(() => import("./components/products/DashboardTEC"));
const Scintillation = lazy(() => import("./components/products/Scintillation"));

// 404
const NotFound = lazy(() => import("./pages/NotFound"));

/* Minimal loading fallback shown while a lazy chunk loads */
function PageLoader() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        background: "var(--bg-page)",
      }}
    >
      <div className="spinner" aria-label="Loading page…" />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>

      {/* ── Skip to main content (keyboard / screen-reader users) ── */}
      <a
        href="#main-content"
        style={{
          position: "absolute",
          top: "-100%",
          left: "1rem",
          zIndex: 9999,
          padding: "0.5rem 1rem",
          background: "var(--busitema-green)",
          color: "#fff",
          borderRadius: "var(--radius-sm)",
          fontWeight: 700,
          textDecoration: "none",
          fontSize: "var(--text-sm)",
          transition: "top 0.1s",
        }}
        onFocus={(e) => { e.currentTarget.style.top = "1rem"; }}
        onBlur={(e)  => { e.currentTarget.style.top = "-100%"; }}
      >
        Skip to main content
      </a>

      {/* ── Persistent top bar ── */}
      <AlertBanner />
      <Navbar />

      {/* ── Page content ── */}
      <main id="main-content" style={{ flex: 1 }} tabIndex={-1}>
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>

          {/* HOME */}
          <Route path="/" element={<Home />} />

          {/* MONITORING PRODUCTS */}
          <Route
            path="/products/dashboardtec"
            element={
              <div>
                <GenericHero title="TEC Dashboard" subtitle="Total Electron Content monitoring over Africa" />
                <DashboardTEC />
              </div>
            }
          />
          <Route
            path="/products/scintillation"
            element={
              <div>
                <GenericHero title="Scintillation Monitor" subtitle="Tracking satellite scintillation" />
                <Scintillation />
              </div>
            }
          />

          {/* ALERTS (NOAA-style scale page) */}
          <Route path="/alerts" element={<SpaceWeatherAlerts />} />

          {/* MAP EXPLORER (SpaceGeo Uganda geocoding) */}
          <Route path="/map" element={<MapExplorer />} />

          {/* DATA PORTAL (IGS-style) */}
          <Route path="/data" element={<DataPortal />} />

          {/* COMMUNITY DASHBOARDS (SWPC-style) */}
          <Route path="/community" element={<CommunityDashboards />} />

          {/* EDUCATION */}
          <Route path="/education" element={<Education />} />

          {/* BENEFITS / IMPACT */}
          <Route
            path="/benefits"
            element={
              <div>
                <GenericHero title="Impact & Benefits" subtitle="How the Space Weather Early Warning Tool protects Uganda's critical infrastructure" />
                <Benefits />
              </div>
            }
          />

          {/* PUBLICATIONS */}
          <Route path="/publications" element={<Publications />} />

          {/* PARTNERS */}
          <Route path="/partners" element={<Partners />} />

          {/* RESOURCES */}
          <Route
            path="/resources"
            element={
              <div>
                <GenericHero title="Resources" subtitle="Project infrastructure, computing, and human resources" />
                <Resources />
              </div>
            }
          />

          {/* ABOUT (standalone route) */}
          <Route
            path="/about"
            element={
              <div>
                <GenericHero title="About the Project" subtitle="National initiative led by Busitema University" />
                <About />
                <Contact />
              </div>
            }
          />

          {/* 404 — catch-all */}
          <Route path="*" element={<NotFound />} />
          <Route path="/data-access-request" element={<DataAccessRequest />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>

      {/* ── Persistent footer ── */}
      <Footer />

      {/* ── Disaster FAB (fixed bottom-right, all pages) ──────────────
          52px circle. Badge shows active count. Red pulse on critical.
          Click → slide-up drawer with top-3 events + "View All" CTA.
          Auto-expands once on first critical event. Idles out in 10s.
      */}
      {SHOW_DISASTER_FAB && <DisasterFAB />}
    </div>
    </ThemeProvider>
  );
}

export default App;
